import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { API_BASE } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, Settings, Loader2, AlertTriangle, Sparkles, Check, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import UpgradeWorkspaceModal from '../components/UpgradeWorkspaceModal';

const PLAN_LABELS = {
  plan_free: 'Free',
  plan_pro_v2: 'Director (Pro)',
  plan_studio_v2: 'Studio',
};
const PLAN_COLORS = {
  plan_free: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  plan_pro_v2: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  plan_studio_v2: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
};

/**
 * formatCancelDate — render an ISO timestamp as "MMM d, yyyy" without
 * pulling in a date library. Returns "soon" as a graceful fallback when
 * the timestamp is missing/unparseable.
 */
const formatCancelDate = (iso) => {
  if (!iso) return 'soon';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'soon';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const PRO_FEATURE_BLURB = {
  plan_pro_v2:    '3 active productions, 5 collaboration seats, script upload, and export',
  plan_studio_v2: '10 active productions, 15 collaboration seats, advanced analytics, and governance',
};

const WorkspaceSettings = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces, updateWorkspace, deleteWorkspace } = useWorkspace();

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Cancel-subscription flow
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // After a successful at-period-end cancel, the backend returns
  // current_period_end as the date the subscription will actually expire.
  // We pin it here so the "Subscription ending {date}" chip can render
  // without an extra fetch — also matches the chip if the user reloads,
  // because in that case workspace.current_period_end on the row covers us.
  const [pendingCancelEndsAt, setPendingCancelEndsAt] = useState(null);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const ws = workspaces.find(w => w.id === workspaceId);
      if (ws) {
        setWorkspace(ws);
        setName(ws.name || '');
        setDescription(ws.description || '');
      } else {
        try {
          const res = await axios.get(`${API_BASE}/workspaces/${workspaceId}`, { headers: getAuthHeaders() });
          setWorkspace(res.data);
          setName(res.data.name || '');
          setDescription(res.data.description || '');
        } catch {
          toast.error('Workspace not found');
          navigate('/workspaces');
        }
      }
      setLoading(false);
    };
    init();
  }, [workspaceId]);

  const isOwner = workspace?.owner_id === user?.id;

  // Default workspace = oldest workspace owned by this user (cannot be deleted)
  const isDefaultWorkspace = (() => {
    const ownedWorkspaces = workspaces
      .filter(w => w.owner_id === user?.id)
      .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    return ownedWorkspaces.length > 0 && ownedWorkspaces[0].id === workspaceId;
  })();

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    try {
      setSaving(true);
      await updateWorkspace(workspaceId, { name: name.trim(), description: description.trim() || null });
      toast.success('Workspace updated');
      setWorkspace(prev => ({ ...prev, name: name.trim(), description: description.trim() || null }));
    } catch (err) {
      toast.error(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== workspace?.name) {
      toast.error(`Type the workspace name to confirm deletion`);
      return;
    }
    try {
      setDeleting(true);
      await deleteWorkspace(workspaceId);
      toast.success('Workspace deleted');
      navigate('/workspaces');
    } catch (err) {
      toast.error(err.message || 'Failed to delete workspace');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * handleCancelSubscription — initiates at-period-end cancellation via
   * POST /api/workspaces/{ws}/upgrade/cancel-subscription. The user keeps
   * their paid features until current_period_end; Razorpay's
   * subscription.cancelled webhook later flips plan_id → plan_free.
   *
   * On 503 (Razorpay env unconfigured): show a friendly inline error.
   * On any other error: surface message but keep the dialog open.
   */
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(
        `${API_BASE}/workspaces/${workspaceId}/upgrade/cancel-subscription`,
        null,
        { headers: getAuthHeaders() }
      );
      const endsAt = res.data?.current_period_end || workspace?.current_period_end || null;
      setPendingCancelEndsAt(endsAt);
      // Reflect locally without a full reload — workspace stays on paid plan
      // until the webhook fires at period-end.
      setWorkspace(prev => prev ? { ...prev, _cancel_pending: true, current_period_end: endsAt } : prev);
      setCancelDialogOpen(false);
      toast.success(`Subscription cancelled. Your workspace will downgrade to Free on ${formatCancelDate(endsAt)}.`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let message;
      if (status === 503) {
        message = 'Subscription management is temporarily unavailable. Please contact support.';
      } else if (status === 400 && typeof detail === 'string' && detail.toLowerCase().includes('no active subscription')) {
        message = 'No active subscription to cancel.';
      } else {
        message = detail || err.message || 'Failed to cancel subscription.';
      }
      toast.error(message);
      // Keep the dialog open so the user sees the error in context.
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-background">
      <main className="container mx-auto px-4 md:px-6 py-8 max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/workspaces/${workspaceId}`)} className="-ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-muted-foreground" />
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">
              {workspace?.name ? `${workspace.name} — Settings` : 'Settings'}
            </h1>
          </div>
        </div>
        {/* General settings */}
        <Card>
          <CardHeader>
            <CardTitle>General</CardTitle>
            <CardDescription>Update your workspace name and description.</CardDescription>
          </CardHeader>
          <CardContent>
            {isOwner ? (
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ws-name">Workspace Name</Label>
                  <Input
                    id="ws-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="My Workspace"
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ws-desc">Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <Input
                    id="ws-desc"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="What is this workspace for?"
                    disabled={saving}
                  />
                </div>
                <Button type="submit" disabled={saving}>
                  {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Saving…</> : 'Save Changes'}
                </Button>
              </form>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Name</p>
                  <p className="font-medium">{workspace?.name}</p>
                </div>
                {workspace?.description && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Description</p>
                    <p className="text-sm">{workspace.description}</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Only the workspace owner can edit settings.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan info */}
        <Card>
          <CardHeader>
            <CardTitle>Plan</CardTitle>
            <CardDescription>Your current workspace plan and features.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className={`px-3 py-1 border ${PLAN_COLORS[workspace?.plan_id] || ''}`}>
                {PLAN_LABELS[workspace?.plan_id] || workspace?.plan_id}
              </Badge>
              {workspace?.plan_id === 'plan_free' && isOwner && (
                <Button variant="default" size="sm" onClick={() => setUpgradeModalOpen(true)}>
                  <Sparkles className="h-4 w-4 mr-1.5" />
                  Upgrade to Pro
                </Button>
              )}
              {workspace?.plan_id !== 'plan_free' && (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                  <Check className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              )}
            </div>
            
            {/* Plan features summary */}
            {workspace?.plan_id === 'plan_free' && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">Free Plan Limits</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• 1 active film</li>
                  <li>• 1 seat (you only)</li>
                  <li>• No script upload</li>
                  <li>• No team collaboration</li>
                </ul>
                <p className="text-xs text-muted-foreground pt-2">
                  Upgrade to Director (Pro) for 3 active productions, 5 seats, script upload, and collaboration.
                </p>
              </div>
            )}
            
            {workspace?.plan_id === 'plan_pro_v2' && (
              <div className="bg-primary/5 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-primary">Director (Pro) Plan Features</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> 3 active productions</li>
                  <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> 5 collaboration seats</li>
                  <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> Script upload enabled</li>
                  <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> Team collaboration</li>
                  <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" /> Export & reports</li>
                </ul>
              </div>
            )}

            {/* Subscription management — paid plans only, owner only.
                Renders either:
                  (a) "Subscription ending {date}" chip + no button, when a cancel is pending, or
                  (b) the "Cancel subscription" button, when billing is healthy.
                Free workspaces and non-owners get nothing here. */}
            {(() => {
              const isPaid = workspace?.plan_id && workspace.plan_id !== 'plan_free';
              const isActiveBilling = workspace?.billing_status === 'active';
              if (!isPaid || !isOwner) return null;
              const endsAt = pendingCancelEndsAt || workspace?.current_period_end;
              const isCancelPending = workspace?._cancel_pending || workspace?.billing_status === 'canceled';

              if (isCancelPending) {
                return (
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                    data-testid="ws-subscription-ending-chip"
                  >
                    <Clock className="h-4 w-4 shrink-0" />
                    <p className="text-sm font-medium">
                      Subscription ending {formatCancelDate(endsAt)}
                    </p>
                  </div>
                );
              }

              // Only show the button when there's an active billing record to cancel.
              if (!isActiveBilling) return null;
              return (
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Cancel anytime — your workspace stays {PLAN_LABELS[workspace.plan_id]} until the current billing period ends.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCancelDialogOpen(true)}
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    data-testid="ws-cancel-subscription-btn"
                  >
                    <XCircle className="h-4 w-4 mr-1.5" />
                    Cancel subscription
                  </Button>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Cancel-subscription confirmation dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={(o) => { if (!cancelling) setCancelDialogOpen(o); }}>
          <DialogContent data-testid="ws-cancel-dialog">
            <DialogHeader>
              <DialogTitle>Cancel subscription?</DialogTitle>
              <DialogDescription className="pt-2 text-foreground">
                Your workspace will remain on <span className="font-semibold">{PLAN_LABELS[workspace?.plan_id]}</span>
                {' '}until <span className="font-semibold">{formatCancelDate(workspace?.current_period_end)}</span>.
                After that, it will downgrade to <span className="font-semibold">Free</span> and lose access to
                {' '}{PRO_FEATURE_BLURB[workspace?.plan_id] || 'paid features'}. You can re-subscribe anytime.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelling}
                autoFocus
                data-testid="ws-cancel-dialog-keep"
              >
                Keep subscription
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                data-testid="ws-cancel-dialog-confirm"
              >
                {cancelling ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Cancelling…</>
                ) : (
                  'Yes, cancel'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Modal */}
        <UpgradeWorkspaceModal
          open={upgradeModalOpen}
          onOpenChange={setUpgradeModalOpen}
          workspaceId={workspaceId}
          workspaceName={workspace?.name}
          onUpgradeComplete={() => {
            // Refresh workspace data
            window.location.reload();
          }}
        />

        {/* Danger zone — owner only, not available for default workspace */}
        {isOwner && !isDefaultWorkspace && (
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Permanently delete this workspace and all its films. This cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Type <span className="font-mono font-semibold text-foreground">{workspace?.name}</span> to confirm.
              </p>
              <Input
                placeholder={`Type "${workspace?.name}" to confirm`}
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                className="border-destructive/50 focus-visible:ring-destructive"
              />
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteConfirm !== workspace?.name || deleting}
                data-testid="delete-workspace-btn"
              >
                {deleting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Deleting…</> : 'Delete Workspace'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Informational notice for default workspace */}
        {isOwner && isDefaultWorkspace && (
          <Card className="border-border/50 bg-muted/30">
            <CardContent className="py-4 flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                This is your default workspace and cannot be deleted. Only additional workspaces you create can be removed.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default WorkspaceSettings;
